import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { SYMPTOMS, ENGINE_TYPES } from "../data/symptoms";
import DiagnosisResult from "../components/DiagnosisResult";
import MediaUpload from "../components/MediaUpload";
import { downloadPdf } from "../lib/media";
import { Car, Bike, ListChecks, MessageSquareText, Loader2, Sparkles, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function Diagnose() {
  const [vehicle, setVehicle] = useState("auto");
  const [engine, setEngine] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [mileage, setMileage] = useState("");
  const [tab, setTab] = useState("sintomi");
  const [selected, setSelected] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [diagId, setDiagId] = useState(null);
  const [photoPath, setPhotoPath] = useState(null);
  const [audioPath, setAudioPath] = useState(null);
  const [audioTranscript, setAudioTranscript] = useState(null);

  const switchVehicle = (v) => {
    setVehicle(v);
    setEngine("");
    setSelected([]);
    setResult(null);
  };

  const toggle = (s) =>
    setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const run = async () => {
    if (selected.length === 0 && !description.trim() && !photoPath && !audioTranscript) {
      toast.error("Seleziona sintomi, descrivi il problema o allega foto/audio");
      return;
    }
    setLoading(true);
    setResult(null);
    setSaved(false);
    setDiagId(null);
    try {
      const { data } = await api.post("/diagnose", {
        vehicle_type: vehicle,
        engine_type: engine || null,
        brand: brand || null,
        model: model || null,
        mileage: mileage || null,
        symptoms: selected,
        description: description || null,
        photo_path: photoPath,
        audio_path: audioPath,
        audio_transcript: audioTranscript,
      });
      setResult(data.result);
      setDiagId(data.id);
      setSaved(true);
      toast.success("Diagnosi completata e salvata nello storico");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore durante la diagnosi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-20 max-w-7xl mx-auto px-5">
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2">Diagnosi Rapida</h1>
      <p className="text-zinc-400 mb-8">Scegli il veicolo, indica i sintomi o descrivi il problema.</p>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* INPUT */}
        <div className="space-y-6">
          {/* vehicle selector */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { id: "auto", label: "Auto", icon: Car },
              { id: "moto", label: "Moto", icon: Bike },
            ].map((v) => (
              <button
                key={v.id}
                data-testid={`vehicle-type-${v.id === "auto" ? "car" : "moto"}-btn`}
                onClick={() => switchVehicle(v.id)}
                className={`rounded-2xl p-5 border flex flex-col items-center gap-2 transition-all ${
                  vehicle === v.id
                    ? "border-[#F97316] bg-[#F97316]/10 shadow-[0_0_24px_rgba(249,115,22,0.25)]"
                    : "border-white/10 bg-[#111823] hover:border-white/25"
                }`}
              >
                <v.icon className={`w-8 h-8 ${vehicle === v.id ? "text-[#F97316]" : "text-zinc-400"}`} />
                <span className="font-display font-bold text-lg">{v.label}</span>
              </button>
            ))}
          </div>

          {/* vehicle details */}
          <div className="rounded-2xl bg-[#111823] border border-white/8 p-5 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-2">Motorizzazione</label>
              <div className="flex flex-wrap gap-2">
                {ENGINE_TYPES[vehicle].map((e) => (
                  <button
                    key={e}
                    data-testid={`engine-${e}`}
                    onClick={() => setEngine(engine === e ? "" : e)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      engine === e ? "border-[#06B6D4] bg-[#06B6D4]/15 text-[#06B6D4]" : "border-white/10 text-zinc-300 hover:border-white/30"
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Marca"
                className="rounded-lg bg-[#0B0F14] border border-white/10 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
              <input data-testid="model-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="Modello"
                className="rounded-lg bg-[#0B0F14] border border-white/10 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
              <input data-testid="mileage-input" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="Km"
                className="rounded-lg bg-[#0B0F14] border border-white/10 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
            </div>
          </div>

          {/* tabs */}
          <div className="flex rounded-lg bg-[#0B0F14] border border-white/10 p-1">
            <button
              data-testid="tab-sintomi"
              onClick={() => setTab("sintomi")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-semibold transition-colors ${
                tab === "sintomi" ? "bg-[#F97316] text-black" : "text-zinc-400"
              }`}
            >
              <ListChecks className="w-4 h-4" /> Sintomi Guidati
            </button>
            <button
              data-testid="tab-descrizione"
              onClick={() => setTab("descrizione")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-semibold transition-colors ${
                tab === "descrizione" ? "bg-[#F97316] text-black" : "text-zinc-400"
              }`}
            >
              <MessageSquareText className="w-4 h-4" /> Descrizione Libera
            </button>
          </div>

          {tab === "sintomi" ? (
            <div className="space-y-5">
              {SYMPTOMS[vehicle].map((grp) => (
                <div key={grp.group}>
                  <h4 className="font-display font-bold text-sm uppercase tracking-wider text-[#06B6D4] mb-2">{grp.group}</h4>
                  <div className="flex flex-wrap gap-2">
                    {grp.items.map((s) => (
                      <button
                        key={s}
                        data-testid={`symptom-${s}`}
                        onClick={() => toggle(s)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          selected.includes(s)
                            ? "border-[#F97316] bg-[#F97316]/15 text-[#F97316]"
                            : "border-white/10 text-zinc-300 hover:border-white/30"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <textarea
              data-testid="free-text-problem-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder="Es. Quando accelero sento un rumore metallico dal lato destro e la spia motore lampeggia..."
              className="w-full rounded-xl bg-[#111823] border border-white/10 p-4 text-sm focus:border-[#F97316] focus:outline-none resize-none"
            />
          )}

          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-wider text-[#06B6D4] mb-2">Allega Foto & Audio (opzionale)</h4>
            <MediaUpload onPhoto={setPhotoPath} onAudio={(p, t) => { setAudioPath(p); setAudioTranscript(t); }} />
          </div>

          <button
            data-testid="submit-diagnosis-button"
            onClick={run}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-black font-bold transition-colors disabled:opacity-60 shadow-[0_0_24px_rgba(249,115,22,0.35)]"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {loading ? "Analisi in corso..." : "Analizza con l'AI"}
          </button>
        </div>

        {/* RESULT */}
        <div className="rounded-2xl bg-[#111823] border border-white/8 p-6 min-h-[300px]">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 gap-4">
              <Loader2 className="w-10 h-10 text-[#F97316] animate-spin" />
              <p className="text-zinc-400 text-sm">Il meccanico AI sta analizzando il tuo veicolo...</p>
            </div>
          ) : result ? (
            <>
              {saved && (
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Salvata nello storico
                  </div>
                  {diagId && (
                    <button
                      data-testid="download-pdf-btn"
                      onClick={() => downloadPdf(diagId)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 hover:border-[#F97316]/60 hover:text-[#F97316] text-sm transition-colors"
                    >
                      <FileDown className="w-4 h-4" /> Scarica PDF
                    </button>
                  )}
                </div>
              )}
              <DiagnosisResult result={result} />
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 gap-3">
              <Sparkles className="w-10 h-10 text-zinc-600" />
              <p className="text-zinc-500 text-sm max-w-xs">
                La diagnosi apparira qui con cause probabili, passi di risoluzione e stima costi.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
